import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { remediationQueries, vendorIssueQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, topBtn, linkBtn } from './VendorDetails';
import RequestRemediationModal from '../Remediation/RequestRemediationModal';
import { RemediationRequestView } from '../Remediation/RemediationDetail';
import RemediationTable, { exportRequests } from '../Remediation/RemediationTable';
import { RemediationStatus, RemediationSummary, apiError, palette } from '../Remediation/remediationShared';

type View = 'active' | 'review' | 'overdue' | 'completed' | 'archived' | 'all';

interface Props {
  vendorId: string;
  vendorName: string;
  vendorDomain?: string | null;
  isDark: boolean;
  onOpenIssues: () => void;
  // The request shown in place of the list (null = the list). Owned by the vendor page so the Issues tab can open one too.
  requestId: string | null;
  onRequestChange: (requestId: string | null) => void;
}

const EMPTY: RemediationSummary[] = [];
const ACTIVE: RemediationStatus[] = ['in_progress', 'awaiting_review'];

/** Vendor sidebar "Remediation": this vendor's remediation requests (the main-sidebar Remediation page shows all vendors).
 *  A request opens here, inside the vendor page, never on the all-vendors page. */
export default function VendorRemediation({ vendorId, vendorName, vendorDomain, isDark, onOpenIssues, requestId, onRequestChange }: Props) {
  const { user } = useAuth();
  const canAct = !!user && user.role !== 'viewer';
  const c = palette(isDark);

  const listQuery = useQuery(remediationQueries.list(vendorId));
  const requests = (listQuery.data?.requests ?? EMPTY) as RemediationSummary[];
  const issuesQuery = useQuery(vendorIssueQueries.issues(vendorId));
  const inRemediation = useQuery(remediationQueries.vendorRisks(vendorId)).data ?? {};

  const [view, setView] = useState<View>('active');
  // A tile is highlighted only after the user picks it, never by default.
  const [pickedTile, setPickedTile] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);

  const post = useMutation({
    mutationFn: async ({ id, path }: { id: string; path: string }) => (await api.post(`/v1/remediation/${id}/${path}`, {})).data,
    meta: { invalidates: [['remediation']] },
  });

  const active = requests.filter(r => ACTIVE.includes(r.status));
  const review = requests.filter(r => r.status === 'awaiting_review');
  const overdue = requests.filter(r => r.overdue);
  const completed = requests.filter(r => r.status === 'completed');
  const archived = requests.filter(r => r.status === 'archived');
  const openRisks = active.reduce((n, r) => n + r.open_risk_count, 0);
  const fixedRisks = active.reduce((n, r) => n + r.fixed_risk_count, 0);

  // Open findings of the latest scan that no open request covers yet.
  const issueNames: string[] = useMemo(() => ((issuesQuery.data?.issues ?? []) as { name: string }[]).map(i => i.name), [issuesQuery.data]);
  const uncovered = issueNames.filter(n => !inRemediation[n]).length;

  const views: { key: View; label: string; count: number; alert?: boolean }[] = [
    { key: 'active', label: 'Active', count: active.length },
    { key: 'review', label: 'Awaiting review', count: review.length, alert: review.length > 0 },
    { key: 'overdue', label: 'Overdue', count: overdue.length, alert: overdue.length > 0 },
    { key: 'completed', label: 'Completed', count: completed.length },
    { key: 'archived', label: 'Archived', count: archived.length },
    { key: 'all', label: 'All', count: requests.length },
  ];
  const q = search.trim().toLowerCase();
  const rows = { active, review, overdue, completed, archived, all: requests }[view]
    .filter(r => !q || [r.title, r.reference, r.created_by_name, ...r.categories, ...r.recipients].some(f => f?.toLowerCase().includes(q)))
    .slice().sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
  const selectedRows = requests.filter(r => selected.has(r.id));
  const remindable = selectedRows.filter(r => r.status === 'in_progress' && r.open_risk_count > 0);
  const archivable = selectedRows.filter(r => r.status !== 'archived');

  const showToast = (text: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ text, tone });
    window.setTimeout(() => setToast(null), 4500);
  };
  const runBulk = async (list: RemediationSummary[], path: 'remind' | 'archive', verb: string) => {
    const results = await Promise.allSettled(list.map(r => post.mutateAsync({ id: r.id, path })));
    const done = results.filter(x => x.status === 'fulfilled').length;
    const firstError = results.find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setSelected(new Set());
    if (done === results.length) showToast(`${verb} ${done} request${done === 1 ? '' : 's'}.`);
    else showToast(`${verb} ${done} of ${results.length}. ${apiError(firstError?.reason, '')}`.trim(), done ? 'ok' : 'error');
  };

  const kpis: { label: string; value: React.ReactNode; color: string; sub: string; subColor?: string; to: View | null }[] = [
    { label: 'Active requests', value: active.length, color: c.ink, sub: active.length ? `${active.length - review.length} waiting on ${vendorName}` : 'nothing in progress', to: 'active' },
    { label: 'Awaiting your review', value: review.length, color: review.length ? c.amber : c.ink,
      sub: review.length ? 'submitted by the vendor' : 'nothing waiting on you', to: 'review' },
    { label: 'Overdue', value: overdue.length, color: overdue.length ? c.danger : c.ink,
      sub: overdue.length ? 'past their due date' : 'all on schedule', subColor: overdue.length ? c.danger : undefined, to: 'overdue' },
    { label: 'Risks fixed', value: active.length ? `${fixedRisks}/${fixedRisks + openRisks}` : '—', color: fixedRisks ? c.green : c.ink,
      sub: active.length ? 'in active requests, confirmed by scans' : 'no active requests', to: 'active' },
    { label: 'Not yet requested', value: issuesQuery.data?.has_scan ? uncovered : '—', color: uncovered ? c.ink : c.green,
      sub: issuesQuery.data?.has_scan ? (uncovered ? 'open issues not in any request' : 'every open issue is covered') : 'no completed scan yet', to: null },
  ];

  if (requestId) {
    return (
      <RemediationRequestView key={requestId} requestId={requestId} embedded backLabel={`${vendorName} remediation`}
        onBack={() => onRequestChange(null)} onDeleted={() => onRequestChange(null)} />
    );
  }

  return (
    <div style={{ fontFamily: sans }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: c.slate }}>
          Requests asking {vendorName} to fix risks found by scans. Later scans confirm each fix on their own.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...topBtn(false, isDark), opacity: rows.length ? 1 : 0.5 }} disabled={!rows.length}
            onClick={() => exportRequests(rows, `${vendorName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-remediation.csv`)}>Export CSV</button>
          {canAct && <button style={topBtn(true, isDark)} onClick={() => setCreating(true)}>Request remediation</button>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        {kpis.map(k => (
          <button key={k.label} onClick={() => { if (!k.to) { onOpenIssues(); return; } setView(k.to); setSelected(new Set()); setPickedTile(pickedTile === k.label ? null : k.label); }}
            style={{ ...card(isDark), padding: '12px 14px', cursor: 'pointer', textAlign: 'left', fontFamily: sans,
              boxShadow: pickedTile === k.label && view === k.to ? `inset 0 0 0 1.5px ${c.ink}` : 'none' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color, marginTop: 6, lineHeight: 1 }}>{listQuery.isPending ? '…' : k.value}</div>
            <div style={{ fontSize: 11, color: k.subColor || c.muted, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.sub}</div>
          </button>
        ))}
      </div>

      <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${c.line}`, padding: '0 14px', overflowX: 'auto' }}>
          {views.map(v => {
            const isActive = view === v.key;
            return (
              <button key={v.key} onClick={() => { setView(v.key); setSelected(new Set()); setPickedTile(null); }} style={{
                background: 'none', border: 'none', borderBottom: `2px solid ${isActive ? c.ink : 'transparent'}`, marginBottom: -1,
                padding: '11px 12px', fontSize: 12, fontWeight: 600, color: isActive ? c.ink : c.slate, cursor: 'pointer', fontFamily: sans, whiteSpace: 'nowrap',
              }}>
                {v.label}
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, marginLeft: 6,
                  background: v.alert && v.count ? '#DC2626' : (isDark ? '#1a1a1a' : '#F1F5F9'), color: v.alert && v.count ? '#fff' : c.slate }}>{v.count}</span>
              </button>
            );
          })}
        </div>

        {requests.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${c.line}` }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search request, REM-…, contact" aria-label="Search requests"
              style={{ padding: '7px 10px', fontSize: 12, fontFamily: sans, color: c.ink, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6, outline: 'none', width: 260, maxWidth: '100%' }} />
            {q && <button style={linkBtn(isDark)} onClick={() => setSearch('')}>Clear</button>}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: c.muted }}>{rows.length} {rows.length === 1 ? 'request' : 'requests'}</span>
          </div>
        )}

        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 14px', borderBottom: `1px solid ${c.line}`,
            background: isDark ? '#0a1628' : T.blueBg, fontSize: 12.5, color: c.ink, flexWrap: 'wrap' }}>
            <strong>{selected.size} selected</strong>
            <button style={{ ...linkBtn(isDark), opacity: remindable.length ? 1 : 0.5 }} disabled={!remindable.length || post.isPending}
              onClick={() => runBulk(remindable, 'remind', 'Reminded')}>Send reminder{remindable.length ? ` (${remindable.length})` : ''}</button>
            <button style={{ ...linkBtn(isDark), opacity: archivable.length ? 1 : 0.5 }} disabled={!archivable.length || post.isPending}
              onClick={() => runBulk(archivable, 'archive', 'Archived')}>Archive</button>
            <button style={{ ...linkBtn(isDark), color: c.muted }} onClick={() => setSelected(new Set())}>Clear selection</button>
          </div>
        )}

        {listQuery.isPending ? (
          <div style={{ padding: 40, textAlign: 'center', color: c.muted, fontSize: 13 }}>Loading requests…</div>
        ) : listQuery.isError ? (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 12.5, color: c.danger }}>
            {apiError(listQuery.error, 'Could not load remediation requests.')} <button style={linkBtn(isDark)} onClick={() => listQuery.refetch()}>Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: c.ink, marginBottom: 4 }}>
              {requests.length === 0 ? 'No remediation requests for this vendor yet' : q ? 'No requests match your search' : `Nothing ${view === 'review' ? 'awaiting your review' : view === 'overdue' ? 'overdue' : `in ${views.find(v => v.key === view)?.label}`}`}
            </div>
            {requests.length === 0 && (
              <div style={{ fontSize: 12.5, color: c.slate, maxWidth: 440, margin: '0 auto', lineHeight: 1.55 }}>
                Pick the risks you want fixed and {vendorName}'s contacts get an invitation to answer in the vendor portal.
              </div>
            )}
            {requests.length === 0 && canAct && issueNames.length > 0 && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
                <button style={topBtn(true, isDark)} onClick={() => setCreating(true)}>Request remediation</button>
                <button style={topBtn(false, isDark)} onClick={onOpenIssues}>Review issues</button>
              </div>
            )}
          </div>
        ) : (
          <RemediationTable rows={rows} isDark={isDark} showVendor={false} selectable={canAct} selected={selected}
            onToggle={(id, on) => setSelected(prev => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; })}
            onToggleAll={on => setSelected(on ? new Set(rows.map(r => r.id)) : new Set())}
            onOpen={r => onRequestChange(r.id)} />
        )}
      </div>

      {creating && (
        <RequestRemediationModal isDark={isDark} vendor={{ id: vendorId, name: vendorName, domain: vendorDomain }}
          onClose={() => setCreating(false)}
          onCreated={id => { setCreating(false); onRequestChange(id); }} />
      )}

      {toast && createPortal(
        <div role="status" style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1400, padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans,
          background: c.surface, color: c.ink, border: `1px solid ${c.line}`, boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.8)' : '0 8px 24px rgba(15,23,42,.12)' }}>
          <span style={{ color: toast.tone === 'ok' ? T.grn : c.danger, fontWeight: 700, marginRight: 8 }}>{toast.tone === 'ok' ? '✓' : '!'}</span>{toast.text}
        </div>,
        document.body,
      )}
    </div>
  );
}
